import type { Editor as TiptapEditor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Fragment } from "@tiptap/pm/model";
import { isBlankInline, isSpacerParagraph } from "../../../../src/shared/proseMirrorNodePredicates";
import { normalizeDocumentSpacing } from "../../../../src/shared/normalizeDocumentSpacing";

// A visual "blank line" exists in documents under three encodings, all of
// which this cleanup must treat as spacing rather than content:
//   1. truly empty paragraphs (Enter-Enter, paste, AI insertion)
//   2. NBSP-only paragraphs (agent markdown round-trips encode empty
//      paragraphs as a single U+00A0 so they survive serialization)
//   3. hardBreak-only paragraphs (<p><br></p> import artifacts)
// Shared predicates keep clipboard serialization and spacing cleanup aligned.

type SegmentPlan = {
  // Inline-child groups, each of which becomes its own paragraph
  segments: PMNode[][];
  changed: boolean;
};

/**
 * Splits a content paragraph's inline children into paragraph segments:
 *  - leading hardBreak runs (with any blank text mixed in) are dropped
 *  - trailing blank runs (hardBreaks and/or blank text) are dropped — the
 *    legacy <p>…<br></p> clipboard/insert encoding
 *  - an interior run containing 2+ hardBreaks is a manual "blank line"
 *    spacing attempt, so it becomes a paragraph boundary
 *  - a single interior hardBreak is an intentional soft line break
 *    (e.g. poetry) and is kept
 */
const segmentParagraphChildren = (node: PMNode): SegmentPlan => {
  const segments: PMNode[][] = [];
  let current: PMNode[] = [];
  let run: PMNode[] = [];
  let runBreaks = 0;
  let changed = false;

  const flushRun = (atEnd: boolean) => {
    if (run.length === 0) return;
    if (atEnd) {
      changed = true;
    } else if (current.length === 0) {
      // Leading run: drop only if it actually contains hardBreaks; a bare
      // blank text node (distinct marks) is left alone.
      if (runBreaks > 0) {
        changed = true;
      } else {
        current.push(...run);
      }
    } else if (runBreaks >= 2) {
      segments.push(current);
      current = [];
      changed = true;
    } else {
      current.push(...run);
    }
    run = [];
    runBreaks = 0;
  };

  node.forEach((child) => {
    if (isBlankInline(child)) {
      run.push(child);
      if (child.type.name === "hardBreak") runBreaks += 1;
    } else {
      flushRun(false);
      current.push(child);
    }
  });
  flushRun(true);
  if (current.length > 0) segments.push(current);

  return { segments, changed };
};

/**
 * Visibility check for the Collapse Empty Paragraphs toolbar button. True
 * only when the document has real content (at least one non-spacer top-level
 * node) AND something the cleanup would change: a blank paragraph, leading or
 * trailing hardBreaks inside a paragraph, or an interior double-hardBreak
 * spacing attempt. An empty editor never shows the button.
 */
export const hasExtraSpacing = (doc: PMNode): boolean => {
  let hasContent = false;
  let needsCleanup = false;
  for (let i = 0; i < doc.childCount; i += 1) {
    const node = doc.child(i);
    if (isSpacerParagraph(node)) {
      needsCleanup = true;
    } else {
      hasContent = true;
      if (!needsCleanup && node.type.name === "paragraph" && segmentParagraphChildren(node).changed) {
        needsCleanup = true;
      }
    }
    if (hasContent && needsCleanup) return true;
  }
  return false;
};

export type SpacingRewrite =
  | { type: "remove"; from: number; to: number }
  | { type: "replace"; from: number; to: number; content: PMNode[] };

/**
 * Plans minimal per-paragraph rewrites for callers that need to inspect the
 * affected ranges. The cleanup action itself uses the shared normalizer and a
 * single replacement step so large documents do not dispatch thousands of
 * ProseMirror steps through the collaboration layer.
 */
export const collectSpacingRewrites = (doc: PMNode): SpacingRewrite[] => {
  const rewrites: SpacingRewrite[] = [];

  doc.forEach((node, offset) => {
    if (node.type.name !== "paragraph") return;

    if (isSpacerParagraph(node)) {
      rewrites.push({ type: "remove", from: offset, to: offset + node.nodeSize });
      return;
    }

    const { segments, changed } = segmentParagraphChildren(node);
    if (!changed) return;

    const content = segments.map((segment) => node.copy(Fragment.from(segment)));
    rewrites.push({ type: "replace", from: offset, to: offset + node.nodeSize, content });
  });

  return rewrites;
};

/**
 * One-click cleanup: removes spacer paragraphs, trims leading/trailing hard
 * breaks, and splits paragraphs at double-hardBreak spacing attempts in a
 * single replacement step — one undo step, and it propagates through the Yjs
 * collaboration layer like any user edit. The same pure normalizer is used by
 * the backend migration engine. Returns how many paragraphs were rewritten
 * (0 means the document was already tidy).
 */
export const removeExtraSpacing = (editor: TiptapEditor): number => {
  if (!editor) return 0;

  const { state } = editor;
  const normalized = normalizeDocumentSpacing(state.doc);
  if (!normalized.changed) return 0;

  const tr = state.tr.replaceWith(
    0,
    state.doc.content.size,
    normalized.document.content,
  );
  editor.view.dispatch(tr.scrollIntoView());
  return normalized.affectedParagraphs;
};
